import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { useCurrency } from '@/lib/platform/contexts/currency-context';
import { getCurrencySymbol } from '@/lib/platform/utils/currency';
import { NumberInput } from '@/components/platform/forms';
import Tooltip from '@/components/platform/ui/Tooltip';
import { tooltipData } from '@/lib/platform/data/tooltip-data';
import type { NumberFormatType } from '@/lib/platform/utils/locale-formatting';
import { AddEntryButton } from './AddEntryButton';
import { useBlankStart } from './use-blank-start';

interface MatchHospitalitySectionProps {
  attendance: number;
  attendanceCpm: number;
  hospitalityGuests: number;
  hospitalityValuePerGuest: number;
  onAttendanceChange: (value: number) => void;
  onAttendanceCpmChange: (value: number) => void;
  onHospitalityGuestsChange: (value: number) => void;
  onHospitalityValuePerGuestChange: (value: number) => void;
  canEdit: boolean;
  numberFormat?: NumberFormatType;
}

export function MatchHospitalitySection({
  attendance,
  attendanceCpm,
  hospitalityGuests,
  hospitalityValuePerGuest,
  onAttendanceChange,
  onAttendanceCpmChange,
  onHospitalityGuestsChange,
  onHospitalityValuePerGuestChange,
  canEdit,
  numberFormat = 'eu',
}: MatchHospitalitySectionProps) {
  const { darkMode } = useDarkMode();
  const { baseCurrency } = useCurrency();
  const [attendanceExpanded, expandAttendance] = useBlankStart(
    attendance !== 0 || attendanceCpm !== 0
  );
  const [hospitalityExpanded, expandHospitality] = useBlankStart(
    hospitalityGuests !== 0 || hospitalityValuePerGuest !== 0
  );

  return (
    <div
      className={`grid grid-cols-1 gap-x-8 gap-y-10 border-b pb-12 md:grid-cols-3 ${darkMode ? 'border-white/10' : 'border-gray-900/10'} ${!canEdit ? 'opacity-70' : ''}`}
    >
      <div>
        <h2 className={`text-base/7 font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
          Match & Hospitality
          <span
            className={`ml-2 rounded px-2 py-1 text-xs font-medium ${
              darkMode ? 'bg-white/10 text-gray-300' : 'bg-black/6 text-gray-600'
            }`}
          >
            HQ
          </span>
        </h2>
        <p className={`mt-1 text-sm/6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Match attendance and hospitality package metrics.
        </p>
        {!canEdit && (
          <span className="mt-2 inline-block rounded bg-red-500/10 px-2 py-1 text-xs font-medium text-red-500">
            Read Only
          </span>
        )}
      </div>

      <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
        {attendanceExpanded ? (
          <>
            {/* Attendance */}
            {/* data-ai-field tags match the AI form bridge field names */}
            <div className="sm:col-span-3" data-ai-field="attendance">
              <NumberInput
                label={
                  <>
                    Attendance
                    <Tooltip {...tooltipData.attendance} wide={true} />
                  </>
                }
                value={attendance}
                onChange={(value) => onAttendanceChange(typeof value === 'number' ? value : 0)}
                decimals={0}
                numberFormat={numberFormat}
                placeholder="0"
                disabled={!canEdit}
              />
            </div>

            {/* Attendance CPM */}
            <div className="sm:col-span-3" data-ai-field="attendance_cpm">
              <NumberInput
                label={
                  <>
                    Attendance CPM
                    <Tooltip {...tooltipData.attendanceCpm} wide={true} />
                  </>
                }
                value={attendanceCpm}
                onChange={(value) => onAttendanceCpmChange(typeof value === 'number' ? value : 0)}
                decimals={2}
                numberFormat={numberFormat}
                suffix={getCurrencySymbol(baseCurrency)}
                placeholder="0"
                disabled={!canEdit}
              />
            </div>
          </>
        ) : (
          <div className="sm:col-span-6">
            <AddEntryButton label="Attendance" onClick={expandAttendance} disabled={!canEdit} />
          </div>
        )}

        {hospitalityExpanded ? (
          <>
            {/* VIP Guests */}
            <div className="sm:col-span-3" data-ai-field="hospitality_guests">
              <NumberInput
                label={
                  <>
                    VIP Guests
                    <Tooltip {...tooltipData.hospitalityGuests} wide={true} />
                  </>
                }
                value={hospitalityGuests}
                onChange={(value) =>
                  onHospitalityGuestsChange(typeof value === 'number' ? value : 0)
                }
                decimals={0}
                numberFormat={numberFormat}
                placeholder="0"
                disabled={!canEdit}
              />
            </div>

            {/* Hospitality Value Per Guest */}
            <div className="sm:col-span-3" data-ai-field="hospitality_value_per_guest">
              <NumberInput
                label={
                  <>
                    Hospitality
                    <Tooltip {...tooltipData.hospitalityValue} wide={true} />
                  </>
                }
                value={hospitalityValuePerGuest}
                onChange={(value) =>
                  onHospitalityValuePerGuestChange(typeof value === 'number' ? value : 0)
                }
                decimals={2}
                numberFormat={numberFormat}
                suffix={getCurrencySymbol(baseCurrency)}
                placeholder="0"
                disabled={!canEdit}
              />
            </div>
          </>
        ) : (
          <div className="sm:col-span-6">
            <AddEntryButton label="Hospitality" onClick={expandHospitality} disabled={!canEdit} />
          </div>
        )}
      </div>
    </div>
  );
}
